import { useState } from 'react';
import { Users, Copy, CheckCircle2 } from 'lucide-react';

export default function MockDataGenerator() {
  const [count, setCount] = useState(5);
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);

  const firstNames = ['John', 'Jane', 'Michael', 'Sarah', 'David', 'Emily', 'Robert', 'Lisa', 'James', 'Maria'];
  const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];
  const domains = ['gmail.com', 'yahoo.com', 'outlook.com', 'icloud.com', 'company.com'];

  const generateMockData = () => {
    const data = Array.from({ length: count }, (_, i) => {
      const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
      const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
      const domain = domains[Math.floor(Math.random() * domains.length)];

      return {
        id: i + 1,
        name: `${firstName} ${lastName}`,
        email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}${i}@${domain}`,
        age: Math.floor(Math.random() * 50) + 18,
        active: Math.random() > 0.5,
        createdAt: new Date(Date.now() - Math.floor(Math.random() * 365) * 24 * 60 * 60 * 1000).toISOString(),
      };
    });

    setOutput(JSON.stringify(data, null, 2));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white dark:from-dark-bg dark:to-dark-surface rounded-2xl p-6 border-2 border-gray-200 dark:border-dark-border shadow-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-xl shadow-lg">
          <Users className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-dark-text">Mock Data Generator</h2>
          <p className="text-sm text-gray-500 dark:text-dark-muted">Generate fake user data for testing</p>
        </div>
      </div>

      <div className="flex items-end gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 block">
            Number of Records
          </label>
          <input
            type="number"
            min="1"
            max="100"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-400 focus:border-transparent transition-all"
          />
        </div>
        <button
          onClick={generateMockData}
          className="px-6 py-3 bg-gradient-to-r from-coral-500 to-teal-500 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
        >
          Generate
        </button>
      </div>

      {output && (
        <>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
              Generated Data ({count} records)
            </label>
            <button
              onClick={copyToClipboard}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-border rounded transition-colors"
            >
              {copied ? (
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              )}
            </button>
          </div>
          <pre className="text-xs font-mono bg-gray-50 dark:bg-dark-bg p-4 rounded-lg border border-gray-300 dark:border-gray-600 overflow-x-auto max-h-96 overflow-y-auto text-gray-900 dark:text-dark-text">
            {output}
          </pre>
        </>
      )}

      {!output && (
        <div className="text-center py-12 text-gray-500 dark:text-dark-muted">
          <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold mb-2">No Data Generated</p>
          <p className="text-sm">Click Generate to create mock user data</p>
        </div>
      )}
    </div>
  );
}
